import classnames from 'classnames/bind'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'

import { useWindowSize } from '@unlikelystudio/react-hooks'
import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import Ratio from '~/components/Abstracts/Ratio'
import SliderControls from '~/components/Abstracts/SliderControls'
import Video from '~/components/Abstracts/Video'
import { useNavBannerContext } from '~/components/Navigation/Navigation/NavBanner'
import { CustomizationPreview } from '~/components/UI/CustomizationPreview'
import { ArrowIcon } from '~/components/UI/Icons'
import Image from '~/components/UI/Image'
import InlineCta from '~/components/UI/InlineCta'
import { PreviewLoader } from '~/components/UI/PreviewLoader'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'

import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import { PRODUCT } from '~/data/dictionary'
import { Media } from '~/data/product-card-data/types'

import FullScreenMedia from '../FullScreenMedia'
import MediaGallery from '../MediaGallery'
import { ProductHeaderProps } from '../ProductHeader'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ProductHeaderMediaProps {
  className?: string
  items?: ProductHeaderProps['media']
  keepMediaRatio?: ProductHeaderProps['keepMediaRatio']
  openPreview?(state: boolean): void
  inPopIn?: boolean
  handleZoom?(state: boolean): void
}

const IMAGE_SIZES = [{ breakpoint: 'lg', ratio: 30 / 60 }, { ratio: 1 }]

function ProductHeaderMedia({
  className,
  items,
  openPreview,
  inPopIn,
  handleZoom,
  keepMediaRatio,
}: ProductHeaderMediaProps) {
  const { previewSources: sources, isMultiBundle } = useProductHeaderProvider()

  // we are using this ref so that we know if the provided medias changed
  // allowing to disable priority on images
  const firstItems = useRef(items)
  const hasUpdatedImages = useRef(false)

  useEffect(() => {
    if (hasUpdatedImages.current) return
    hasUpdatedImages.current =
      JSON.stringify(items) !== JSON.stringify(firstItems.current)
  }, [items])

  const { activeTabIndex, currentVariant } =
    useGetStateInformationsFromUserSelection()

  const previewSources = useMemo(
    () => Object.values(sources?.[activeTabIndex] ?? {}).filter(Boolean) ?? [],
    [JSON.stringify(sources)],
  )

  const t = useTranslate()
  const withPreview = previewSources?.length > 0

  const processedItems = useMemo(
    () =>
      withPreview
        ? [items?.[0]]
        : items.map((item) => ({
            ...item,
            image: {
              ...item.image,
              asPlaceholder: hasUpdatedImages.current,
              priority: !hasUpdatedImages.current,
            },
          })),
    [JSON.stringify(items)],
  )

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const [desktopImageIndex, setCurrentDesktopImageIndex] = useState(null)
  const [zoomIsOpen, setZoomIsOpen] = useState(false)
  const windowRect = useWindowSize()

  const isMobile = windowRect.width < 930

  const globalData = useGlobalData()
  const navTheme = useStyle({ color: globalData?.theme?.nav })
  const setNextImage = (n) => {
    const length = items?.length
    const newImageIndex = (((desktopImageIndex + n) % length) + length) % length
    length && setCurrentDesktopImageIndex(newImageIndex)
  }

  const [
    { slideIndex: slideIndexMobile, setSlideIndex },
    setSliderStateMobile,
  ] = useSliderState()

  const [
    {
      prevSlide,
      nextSlide,
      slideIndex,
      enableDrag,
      maxSlideIndex,
      setSlideIndex: setSlideDesktop,
    },
    setSliderStateDesktop,
  ] = useSliderState()

  useEffect(() => {
    handleZoom?.(zoomIsOpen)
  }, [zoomIsOpen])

  useEffect(() => {
    setSlideDesktop(0)
    setSlideIndex(0)
  }, [JSON.stringify(previewSources), JSON.stringify(items)])

  const [withBanner] = useNavBannerContext()
  const headerMediaTriggerStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12PrimaryReg,
  })
  const previewImages = withPreview
    ? [
        previewSources.map((source) => ({
          image: { src: source?.src, width: 1536, height: 2048, loader: null },
        })) as Media[],
      ].concat(processedItems.map((item) => [item] as Media[]))
    : items.map((item) => [item] as Media[])

  if (items?.length === 0 && !withPreview) return null
  return (
    <div
      className={cx(css.ProductHeaderMedia, className, {
        withBanner,
        inPopIn,
        keepMediaRatio,
      })}>
      <div className={cx(css.bundleCtaContainer)}>
        <InlineCta
          className={cx(css.cta, headerMediaTriggerStyle)}
          theme={GlobalThemeColors.Black}
          onClick={() => openPreview?.(true)}>
          {t(PRODUCT.PREVIEW_BUNDLE)}
        </InlineCta>
      </div>
      {items?.length > 0 && (
        <>
          <div className={cx(css.mobileSliderContainer, css.hideOnLargeScreen)}>
            <Slider
              className={cx(css.mobileSlider)}
              snap={true}
              containerClassName={css.sliderContainer}
              maxSlideIndexChange={1}
              setSliderState={setSliderStateMobile}
              infinite={items.length > 1}>
              {items?.map((item, index) => {
                return index === 0 && withPreview ? (
                  <CustomizationPreview
                    key={`header_mobile_item_${index}`}
                    onClick={() => {
                      setZoomIsOpen(true)
                    }}
                    className={cx(css.media)}
                    sources={previewSources}
                  />
                ) : (
                  <Ratio
                    onClick={() => {
                      setZoomIsOpen(true)
                    }}
                    preset={
                      inPopIn
                        ? 'product-header-bundle-ratio'
                        : 'product-header-slider-ratio'
                    }
                    key={index}
                    className={css.media}>
                    {(className) =>
                      item?.video ? (
                        <Video
                          className={cx(className)}
                          src={item?.video}
                          loop
                          loading="lazy"
                          autoPlay
                          playsInline
                          muted
                          objectFit="cover"
                        />
                      ) : (
                        <div key={JSON.stringify(item?.image)}>
                          <Image
                            key={item?.image?.src}
                            className={cx(css.mobileImage, className)}
                            layout="fill"
                            objectFit="cover"
                            asPlaceholder={
                              (hasUpdatedImages.current && index === 0) ||
                              index > 0
                            }
                            priority={!hasUpdatedImages.current && index === 0}
                            sizesFromBreakpoints={IMAGE_SIZES}
                            quality={90}
                            {...item?.image}
                          />
                        </div>
                      )
                    }
                  </Ratio>
                )
              })}
            </Slider>
            <div className={cx(css.mobileSliderControlsContainer, gridStyle)}>
              <div className={cx(css.column, navTheme)}>
                {/* Disabled for V1 */}
                {/* <AddToWishlistButton className={css.addToWishlist} /> */}
                {items?.length > 1 && (
                  <SliderControls
                    length={items?.length}
                    className={css.mobileSliderControls}
                    slideIndex={slideIndexMobile}
                    setSlideIndex={setSlideIndex}
                  />
                )}
              </div>
            </div>
          </div>
          {/* Desktop current item */}
          <div className={cx(css.containerActiveSlide, css.hideOnSmallScreen)}>
            <Ratio
              ratio={0.75}
              onClick={() => {
                openPreview?.(true)
              }}>
              {() => (
                <>
                  {withPreview && slideIndex === 0 && (
                    <CustomizationPreview
                      key={`item_desktop_0`}
                      sources={previewSources}
                    />
                  )}
                  {processedItems?.map((item, index) => {
                    const currentIndex = withPreview ? index + 1 : index

                    return (
                      currentIndex === slideIndex &&
                      (item?.video ? (
                        <Video
                          className={cx(className)}
                          key={`item_desktop_${index}`}
                          src={item?.video}
                          loop
                          loading="lazy"
                          autoPlay
                          playsInline
                          muted
                          objectFit="cover"
                        />
                      ) : (
                        <Image
                          key={`${item?.image?.src}_${currentVariant?.id}`}
                          className={cx(css.image)}
                          layout="fill"
                          objectFit="cover"
                          draggable="false"
                          quality={90}
                          asPlaceholder={hasUpdatedImages.current}
                          priority={!hasUpdatedImages.current}
                          {...item?.image}
                          sizesFromBreakpoints={IMAGE_SIZES}
                        />
                      ))
                    )
                  })}
                </>
              )}
            </Ratio>
          </div>
          {/* Desktop slider little selectors */}
          <div className={cx(css.navContainer, css.hideOnSmallScreen)}>
            <div className={cx(css.nav)}>
              {enableDrag && slideIndex !== 0 && (
                <div className={cx(css.prev)} onClick={prevSlide}>
                  <ArrowIcon />
                </div>
              )}
              <Slider
                className={css.sliderSelector}
                setSliderState={setSliderStateDesktop}
                axis="y"
                snap>
                {items?.map((item, key) => {
                  return (
                    item?.image && (
                      <div
                        key={`selector_item_${key}`}
                        className={cx(css.navItem, {
                          active: key === slideIndex,
                        })}
                        onClick={() => {
                          setSlideDesktop(key)
                        }}>
                        {key === 0 && (
                          <PreviewLoader className={cx(css.loader)} />
                        )}
                        <Image
                          key={item?.image?.src}
                          className={cx(css.image)}
                          ratio={3 / 4}
                          sizesFromBreakpoints={IMAGE_SIZES}
                          draggable="false"
                          layout="fill"
                          asPlaceholder
                          quality={90}
                          objectFit="cover"
                          {...item?.image}
                        />
                      </div>
                    )
                  )
                })}
              </Slider>
              {enableDrag &&
                slideIndex !== maxSlideIndex &&
                items?.length > 3 && (
                  <div className={cx(css.next)} onClick={nextSlide}>
                    <ArrowIcon />
                  </div>
                )}
            </div>
          </div>
        </>
      )}

      {zoomIsOpen &&
        (!isMobile ? (
          <FullScreenMedia
            key={`fullScreenImage${desktopImageIndex}`}
            onPreviousClick={() => setNextImage(-1)}
            onNextClick={() => setNextImage(1)}
            onExitFullscreen={() => setZoomIsOpen(false)}
            images={previewImages?.[desktopImageIndex]}
          />
        ) : (
          <MediaGallery
            setImageIndex={setSlideIndex}
            imageIndex={slideIndexMobile}
            onExitFullscreen={() => setZoomIsOpen(false)}
            images={previewImages}
          />
        ))}
    </div>
  )
}

ProductHeaderMedia.defaultProps = {}

export default ProductHeaderMedia
